import { Text, type NumberInputProps } from '@mantine/core';

import { usePreviousValue } from './previousValues.js';

/** Identisch zum `maw`-Typ von Mantines `NumberInput` -- `ValidatedNumberInput` reicht ihn 1:1 durch. */
type MawValue = NumberInputProps['maw'];

/**
 * Die einzige Definition der Vorwert-Optik (D-12/D-13) -- jede weitere Anzeigestelle (D-15,
 * Plaene 24-07/24-08) rendert `PreviousValueLine` selbst, statt Farbe und Groesse erneut zu
 * setzen. `previousValues.ts` bleibt bewusst JSX-frei; diese Datei ist deshalb eigenstaendig
 * und importiert nur den Leser, nicht `validatedFields.tsx`.
 */

/**
 * Innerer rechter Feldrand eines Mantine-`Input` in der Standardgroesse `sm`
 * (`--input-height-sm: 36px`, `--input-padding: calc(var(--input-height) / 3)` -> 12px,
 * siehe `theme/colors.ts`-Nachbarschaft `styles/Input.css:6-26`). Kein Formular des Projekts
 * setzt heute eine andere Feldgroesse; die Zahl ist deshalb keine geratene Konstante.
 */
export const PREVIOUS_VALUE_INSET = 12;

/**
 * Die Optik: unter dem Feld, rechtsbuendig am inneren Feldrand, orangen Farbton, klein.
 * Nur die nackte Zahl (D-13) -- kein Praefix, keine Einheit, kein Delta. Der Text kommt
 * bereits formatiert aus dem Store (D-14, Formatierung beim Befuellen); diese Komponente
 * formatiert nichts nach.
 *
 * Der Umbruchschutz im Stil-Objekt unten ist die Vorsorge fuer die 90px schmale
 * Formular-D-Matrixspalte: dort ist 90px ein `minWidth` an der Kopfzelle, kein Maximum, und
 * die Tabelle scrollt bereits waagerecht -- ein Umbruch mitten in einer Zahl waere die
 * schlechtere Loesung.
 *
 * Bewusst OHNE einen Feldstil, der Zeigerereignisse abschaltet: der Vorwert liegt in
 * derselben Box, die Hover und Fokus des Feldes traegt (siehe `FieldMessageTooltip`); wer
 * dort hovert, zielt praktisch auf das Feld, und die Sprechblase zeigt dessen eigene
 * Meldung mit (D-20). Die Ausnahme waere Zusatzcode ohne Gewinn. Nicht zu verwechseln mit
 * dem umgekehrt gelagerten Fall in `AccidentMatrixSection.tsx:62-68`: dort macht derselbe
 * Feldstil-Name an einer readOnly/disabled-Zelle den Hinweis der Jahresarbeitsstunden
 * ueberhaupt erst erreichbar und traegt seit Plan 20-15 ein "NICHT ENTFERNEN" -- ein
 * anderer Zweck an einer anderen Stelle.
 *
 * `maw` (Befund 24-09, Browser-Abnahme -- kein D-Punkt): Der hoerende `Box`-Wrapper aus
 * `FieldMessageTooltip` steht in Formularen mit Einspalten-Liste (Formular AB, C) in einem
 * `Stack` (Flex-Column, `align-items: stretch`) und streckt sich deshalb auf die VOLLE
 * Stack-Breite -- unabhaengig vom `maw` des eigentlichen Eingabefelds. Ohne eigenes `maw`
 * an DIESEM Element rechtsbuendig sich der Vorwert also an der Stack-Kante aus, nicht am
 * Feldrand: am laufenden Server gemessen 1118px statt 560px, der Vorwert landet weit
 * rechts vom Feld statt darunter. `alignSelf: 'flex-start'` am Wrapper wurde geprueft und
 * verworfen -- es kollabiert das `NumberInput` selbst auf seine Inhalts-Breite (Shrink-to-
 * fit-Kollision mit dessen eigenem `width: 100%`), aendert also sichtbar die Feldbreite.
 * Ein `maw` NUR an diesem `Text` (normaler Block, keine Shrink-to-fit-Bahn) faengt die
 * Breite dagegen exakt am selben Wert wie das Feld ab, ohne dessen Layout zu beruehren.
 */
export function PreviousValueLine({ value, maw }: { value: string; maw?: MawValue }) {
  return (
    <Text
      fz={10}
      c="vdzOrange.6"
      ta="right"
      pr={PREVIOUS_VALUE_INSET}
      mt={2}
      lh={1.2}
      maw={maw}
      style={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}
    >
      {value}
    </Text>
  );
}

/**
 * Der feldgebundene Leser: holt sich den Vorwert von `field` selbst und gibt `null` zurueck,
 * solange keiner vorliegt (Schalter aus, keine Vorjahresmeldung, Feld nicht im Store). Nur
 * so bleibt die Feldhoehe unveraendert, wenn kein Vorwert existiert -- es entsteht keine
 * leere Zusatzzeile.
 *
 * `maw` reicht `ValidatedNumberInput` unveraendert durch (Befund 24-09, siehe
 * `PreviousValueLine` oben) -- ohne eigenes `maw` bleibt das Verhalten wie zuvor (kein
 * Feld dieses Projekts ausserhalb der Einspalten-Listen setzt heute ein `maw`).
 */
export function PreviousValueForField({ field, maw }: { field: string; maw?: MawValue }) {
  const value = usePreviousValue(field);
  if (value === undefined) {
    return null;
  }
  return <PreviousValueLine value={value} maw={maw} />;
}
